import {
  CalendarToday,
  CheckCircle,
  Close,
  LocationOn,
  Schedule,
  Search,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import apiService from "../../../../../../../service/Api";
import { RootState } from "../../../../../../../store/Index";
import { tokens } from "../../../../../../../theme";

interface StreamResult {
  id: number;
  title: string;
  date: string;
  start: string;
  end: string;
  address_one: string;
  city: string;
  suburb: string;
  website_stream_link: string;
  stream_thumbnail_background_image: string | null;
  booking: {
    deceased?: Array<{ first_name?: string; surname?: string }>;
  };
}

interface StreamModalProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (streams: { id?: string; title: string; url: string }[]) => void;
}

const getDuration = (start: string, end: string): string => {
  if (!start || !end) return "";
  const toMins = (t: string) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };
  let diff = toMins(end) - toMins(start);
  if (diff < 0) diff += 24 * 60;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function StreamModal({
  open,
  onClose,
  onGenerate,
  venueId,
}: StreamModalProps & { venueId?: number | null }) {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const isDark = theme.palette.mode === "dark";

  const [streams, setStreams] = useState<StreamResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedYear, setSelectedYear] = useState<string>("All");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const fetch = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page_size: "1000" });
        if (venueId) params.set("venue", String(venueId));

        const res = await apiService.get<{ results: StreamResult[] }>(
          `/api/streams/stream/?${params.toString()}`,
        );
        if (!cancelled) setStreams(res.data.results || []);
      } catch {
        if (!cancelled) setStreams([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => {
      cancelled = true;
    };
  }, [open, venueId]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setSelectedYear("All");
      setSelectedIds(new Set());
    }
  }, [open]);

  const years = useMemo(() => {
    const set = new Set<string>();
    streams.forEach((s) => {
      if (s.date) set.add(new Date(s.date).getFullYear().toString());
    });
    return ["All", ...Array.from(set).sort((a, b) => Number(b) - Number(a))];
  }, [streams]);

  const filtered = useMemo(() => {
    return streams.filter((s) => {
      const matchesSearch =
        !search ||
        s.title.toLowerCase().includes(search.toLowerCase()) ||
        s.city?.toLowerCase().includes(search.toLowerCase()) ||
        s.address_one?.toLowerCase().includes(search.toLowerCase());
      const matchesYear =
        selectedYear === "All" ||
        new Date(s.date).getFullYear().toString() === selectedYear;
      return matchesSearch && matchesYear;
    });
  }, [streams, search, selectedYear]);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleGenerate = () => {
    const selected = streams
      .filter((s) => selectedIds.has(s.id))
      .map((s) => ({
        id: String(s.id),
        title: s.title,
        url:
          s.website_stream_link ||
          `${window.location.origin}/memorial-page/${s.id}/${encodeURIComponent(
            (s.title || "")
              .replace(/^Stream for /i, "")
              .trim()
              .replace(/\s+/g, "-"),
          )}`,
      }));
    onGenerate(selected);
    onClose();
  };

  const paperBg = colors.primary[900];
  const textPrimary = colors.primary[700];
  const textMuted = colors.primary[300];
  const accent = colors.primary[400];
  const cardBg = isDark ? colors.primary[800] : "#fff";
  const borderColor = isDark ? colors.primary[300] + "20" : "#e5e7eb";
  const hoverBg = isDark ? colors.primary[300] + "10" : colors.primary[800];
  const chipBg = isDark ? colors.primary[800] : colors.primary[600];
  const chipActive = accent;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          bgcolor: paperBg,
          boxShadow: isDark
            ? "0 24px 64px rgba(0,0,0,0.6)"
            : "0 24px 64px rgba(0,0,0,0.12)",
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={0.5}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color={textPrimary}>
              Add Previous Streams
            </Typography>
            <Typography variant="body2" sx={{ color: textMuted, mt: 0.3 }}>
              Select streams to showcase in this proposal.
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: textMuted, "&:hover": { bgcolor: hoverBg } }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        {/* Search */}
        <TextField
          fullWidth
          placeholder="Search streams..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{
            mt: 2,
            mb: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: "50px",
              bgcolor: isDark ? colors.primary[800] : colors.primary[800],
              "& fieldset": { borderColor },
              "&:hover fieldset": { borderColor: accent },
              "&.Mui-focused fieldset": { borderColor: accent },
            },
            "& input": { color: textPrimary, fontSize: "13px" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: textMuted }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Year filters */}
        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
          {years.map((year) => (
            <Chip
              key={year}
              label={year}
              onClick={() => setSelectedYear(year)}
              size="small"
              sx={{
                borderRadius: "50px",
                fontWeight: 600,
                fontSize: "12px",
                cursor: "pointer",
                bgcolor: selectedYear === year ? chipActive : chipBg,
                color:
                  selectedYear === year ? colors.primary[900] : textPrimary,
                border: "none",
                "&:hover": {
                  bgcolor: selectedYear === year ? chipActive : hoverBg,
                },
              }}
            />
          ))}
        </Stack>

        <Box
          sx={{
            height: 420,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            pr: 0.5,
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: borderColor,
              borderRadius: "4px",
            },
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress size={28} sx={{ color: accent }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="body2" sx={{ color: textMuted }}>
                No streams found
              </Typography>
            </Box>
          ) : (
            filtered.map((stream) => {
              const isSelected = selectedIds.has(stream.id);
              const duration = getDuration(stream.start, stream.end);
              const venue = [stream.address_one, stream.city]
                .filter(Boolean)
                .join(", ");

              return (
                <Box
                  key={stream.id}
                  onClick={() => toggleSelect(stream.id)}
                  sx={{
                    borderRadius: "14px",
                    border: "1.5px solid",
                    borderColor: isSelected ? accent : borderColor,
                    bgcolor: cardBg,
                    cursor: "pointer",
                    overflow: "hidden",
                    flexShrink: 0,
                    transition: "all 0.15s ease",
                    position: "relative",
                    "&:hover": {
                      borderColor: accent,
                      boxShadow: `0 0 0 3px ${accent}18`,
                    },
                  }}
                >
                  {/* Thumbnail */}
                  <Box
                    sx={{
                      width: "100%",
                      height: 120,
                      bgcolor: colors.primary[800],
                      backgroundImage: stream.stream_thumbnail_background_image
                        ? `url(${stream.stream_thumbnail_background_image})`
                        : undefined,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      position: "relative",
                    }}
                  >
                    {isSelected && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          bgcolor: accent,
                          borderRadius: "50%",
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CheckCircle sx={{ fontSize: 20, color: "#fff" }} />
                      </Box>
                    )}
                    {!stream.stream_thumbnail_background_image && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: textMuted,
                            fontStyle: "italic",
                          }}
                        >
                          No thumbnail
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Info */}
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      fontSize="14px"
                      color={textPrimary}
                      mb={0.8}
                    >
                      {stream.title}
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      {venue && (
                        <Stack
                          direction="row"
                          spacing={0.4}
                          alignItems="center"
                        >
                          <LocationOn sx={{ fontSize: 13, color: textMuted }} />
                          <Typography
                            variant="caption"
                            sx={{ color: textMuted }}
                          >
                            {venue}
                          </Typography>
                        </Stack>
                      )}
                      {stream.date && (
                        <Stack
                          direction="row"
                          spacing={0.4}
                          alignItems="center"
                        >
                          <CalendarToday
                            sx={{ fontSize: 12, color: textMuted }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: textMuted }}
                          >
                            {formatDate(stream.date)}
                          </Typography>
                        </Stack>
                      )}
                      {duration && (
                        <Stack
                          direction="row"
                          spacing={0.4}
                          alignItems="center"
                        >
                          <Schedule sx={{ fontSize: 12, color: textMuted }} />
                          <Typography
                            variant="caption"
                            sx={{ color: textMuted }}
                          >
                            {duration}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer */}
        <Stack direction="row" justifyContent="flex-end" mt={2.5} spacing={1}>
          <Button
            onClick={onClose}
            variant="text"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: textMuted,
              "&:hover": { bgcolor: hoverBg },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            variant="contained"
            disabled={selectedIds.size === 0}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              bgcolor: accent,
              color: colors.primary[900],
              "&:hover": { bgcolor: colors.primary[500] },
              "&:disabled": { opacity: 0.5 },
            }}
          >
            Generate{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
